/**
 * Row envelopes — a unified, DOM-free, message-passable protocol for everything
 * a consumer can do to a row.
 *
 * An `Envelope` is one declarative interaction: a composed OPTION (set Format /
 * Hours), an INTRINSIC mutation (set title / height / delete), or a CAPABILITY
 * action (alias reset-to-template / edit-as-group; source lookup-key). A headless
 * consumer hydrates a sheet, calls `row.envelopes()` to INTERROGATE the list
 * ("virtually click the row") and reads each envelope's current value via
 * `input` — all with no DOM. The DOM popover is just another consumer.
 *
 * DRIVING an envelope is the SDK row's verb, not the model row's:
 * `sdkRow.envelopes.apply({ key, value }, actor)` (`RowEnvelopes.Driver`) resolves the
 * key, runs the checked write, persists the intrinsic edits through the row's
 * own verbs (`update`) and mirrors the visible-row attribute patch itself — so
 * no caller mirrors by hand. The rows-side half it runs is `RowEnvelopeDrive`
 * below, reached through `@tento-lona/sheets/internal-sdk` only.
 *
 * The functions take the ROW, so no caller threads a `sheet`. The write
 * `Target` + `reRender` hint stay INTERNAL — a consumer never sees them.
 * `ModelRow.envelopes()` is the same list as `RowEnvelopes.forRow`.
 *
 * Design: lona-sdk-2.0.project.docs/content/row-options-composition.md and the
 * "Row Envelopes" plan.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { sheetOf, sheetStores } from "../sheet/sheet-stores.ts";
import type { ModelRow } from "./model-row.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import { type ModelRowReads } from "./model-row-reads.ts";
import { liveRowOf } from "./model-row.ts";
import { ModelRowOptions, writeOptionTarget } from "./row-options.ts";
import { RowProvider } from "./row-provider.ts";
import { AliasRow, aliasWriterOf } from "./variants/alias-row.ts";
import { SourceRow } from "./variants/source-row.ts";
import { isSyntheticDefaultRendererId } from "../ids/row-local-id.ts";
import { RowLocalEdits } from "./row-local-edits.ts";
import { SdkRequestError } from "../core/errors.ts";
import type { Actor } from "../tx/invalidation.ts";
import type { RowPatch } from "./row-patch.ts";
import { isRange } from "./row-options.ts";

/**
 * The edits a row offers (title, height, delete, …), each with where it
 * surfaces and how it is applied.
 */
export namespace RowEnvelopes {
  /**
   * The "default shown vs hidden" flag — where an envelope surfaces.
   * - `shown`    — on the row itself, without opening its menu (e.g. title).
   * - `menu`     — when the row's menu is opened (most options, delete, height).
   * - `advanced` — full-inspection only (lookupKey, raw metadata).
   */
  export type Visibility = "shown" | "menu" | "advanced";

  /**
   * What an envelope accepts plus its CURRENT value — one discriminated union so
   * a consumer reads `kind` and `value` together. `action` is a fire-once
   * command (delete / reset) with no value.
   */
  export type Input =
    | { readonly kind: "action" }
    | { readonly kind: "toggle"; readonly value: boolean }
    | { readonly kind: "text"; readonly value: Option<string> }
    | {
        readonly kind: "code";
        readonly value: Option<string>;
        readonly language: "plain" | "lisp";
      }
    | {
        readonly kind: "number";
        readonly value: Option<number>;
        readonly min: Option<number>;
        readonly max: Option<number>;
      }
    | { readonly kind: "color"; readonly value: string }
    | {
        readonly kind: "select";
        readonly value: Option<string>;
        readonly options: readonly {
          readonly value: string;
          readonly label: string;
        }[];
      }
    | {
        readonly kind: "time-range";
        readonly value: { readonly start: number; readonly end: number };
        readonly min: number;
        readonly max: number;
      }
    | { readonly kind: "timezone"; readonly value: Option<string> };

  /**
   * One thing a consumer can do to a row. Discover it via `row.envelopes()`,
   * read its current value via `input`, apply a new one through the SDK row
   * (`sdkRow.envelopes.apply({ key, value }, actor)`). The write path is
   * internal.
   */
  export interface Envelope {
    /** Stable id, e.g. `"number:format"` | `"row:title"` | `"row:delete"`. */
    readonly key: string;
    readonly label: string;
    readonly visibility: Visibility;
    /** Section within the menu (e.g. `"Format"`); `null` for ungrouped. */
    readonly group: Option<string>;
    readonly input: Input;
    /** Whether the envelope applies to this row right now (editable / state). */
    readonly enabled: boolean;
  }

  /** The serializable mutation a (possibly remote) consumer sends to a row. */
  export interface Message {
    readonly key: string;
    /** Ignored for `action` inputs. */
    readonly value: ModelRowOptions.Value;
  }

  /**
   * What `sdkRow.envelopes.apply` resolves with, tagged by `kind`; both say
   * how the host should re-render (the DOM acts on it; a headless consumer
   * ignores it):
   * - `"applied"` — the write ran and was accepted; `persisted` says where
   *   the change lives;
   * - `"rejected"` — the message was not applied and nothing was sent
   *   (`why`: unknown key, invalid value, a disabled or app-routed envelope,
   *   a formula cycle).
   * A write the backend refused or never answered is neither: `apply`
   * REJECTS with the backend's `SdkRequestError`, like every checked verb.
   */
  export type Applied = Applied.Accepted | Applied.Rejected;

  export namespace Applied {
    /** The write ran and the backend (or the local model) took it. */
    export interface Accepted {
      readonly kind: "applied";
      readonly reRender: ModelRowOptions.ReRender;
      /** Where the applied change lives. */
      readonly persisted: Persisted;
    }

    /** The message was not applied: nothing changed, nothing was sent. */
    export interface Rejected {
      readonly kind: "rejected";
      readonly reRender: ModelRowOptions.ReRender;
      /** Why the message was not applied. */
      readonly why: string;
    }

    /**
     * Where an applied change lives: `"backend"` — the backend accepted it;
     * `"deferred-to-promote"` — the sheet is ephemeral, and the change
     * persists with the sheet when it is promoted; `"local-only"` — an
     * envelope that edits the local model only (the sync-logs view toggle).
     */
    export type Persisted = "backend" | "deferred-to-promote" | "local-only";
  }
}

// ── intrinsic + capability spec registry ────────────────────────────────

const HEIGHT_MIN = 24;
const HEIGHT_MAX = 960;

/**
 * A non-option envelope: an intrinsic mutation (title/height/delete) or a
 * capability action (alias reset/detach; source lookupKey). Each carries its
 * own read/apply closures over the rows-side methods, so `ModelRowOptions.Target`
 * stays options-only.
 */
interface Spec {
  readonly key: string;
  readonly label: string;
  readonly visibility: RowEnvelopes.Visibility;
  readonly group: Option<string>;
  readonly control: ModelRowOptions.ControlSpec;
  readonly reRender: ModelRowOptions.ReRender;
  /** Capability/state gate — whether the envelope exists for this row. */
  readonly applies: (row: ModelRow) => boolean;
  /** Current value for `input` (`null` for actions). */
  readonly read: (row: ModelRow) => ModelRowOptions.Value;
  /** Editable + per-row capability. */
  readonly enabled: (row: ModelRow) => boolean;
  /** Mutate the row, or return why the value was rejected (validation / app-routed). */
  readonly apply: (
    row: ModelRow,
    value: ModelRowOptions.Value,
    intrinsics: RowEnvelopeDrive.Intrinsics,
  ) => Promise<Option<string>>;
  /**
   * Where `apply` persists: through the sheet's backend, the local model
   * only, or the driver's intrinsic verbs (`RowEnvelopeDrive.Intrinsics`).
   */
  readonly persists: "backend" | "local-only" | "intrinsic";
}

const SPECS: readonly Spec[] = [
  {
    key: "row:title",
    label: "Title",
    visibility: "shown",
    group: null,
    control: { kind: "text" },
    reRender: "cells",
    applies: () => true,
    read: (row) => row.label ?? null,
    enabled: (row) => row.sheetReads.editable && row.capabilities().canEditTitle,
    persists: "intrinsic",
    apply: async (row, value, intrinsics) => {
      if (typeof value !== "string") return "title requires a string";
      return await intrinsics.setLabel(row, value);
    },
  },
  {
    key: "row:height",
    label: "Height",
    visibility: "menu",
    group: null,
    control: { kind: "range", min: HEIGHT_MIN, max: HEIGHT_MAX },
    reRender: "layout",
    applies: () => true,
    read: (row) => row.heightPx ?? null,
    enabled: (row) => row.sheetReads.editable && row.capabilities().canEditHeight,
    persists: "intrinsic",
    apply: async (row, value, intrinsics) => {
      if (typeof value !== "number") return "height requires a number";
      return await intrinsics.setHeight(row, value);
    },
  },
  {
    key: "row:delete",
    label: "Delete",
    visibility: "menu",
    group: null,
    control: { kind: "action" },
    reRender: "structure",
    applies: () => true,
    read: () => null,
    enabled: (row) => row.sheetReads.editable && row.capabilities().canDelete,
    persists: "intrinsic",
    apply: async (row, _value, intrinsics) => await intrinsics.remove(row),
  },
  {
    key: "alias:edit-as-group",
    label: "Edit as group",
    visibility: "menu",
    group: null,
    control: { kind: "action" },
    reRender: "structure",
    applies: (row) => row instanceof AliasRow && !row.instance().hydrated,
    read: () => null,
    enabled: (row) => row.sheetReads.editable,
    persists: "backend",
    apply: async (row) => {
      if (!(row instanceof AliasRow)) return "not an alias row";
      await aliasWriterOf(row).detach();
      return null;
    },
  },
  {
    key: "alias:reset",
    label: "Reset to template",
    visibility: "menu",
    group: null,
    control: { kind: "action" },
    reRender: "structure",
    applies: (row) => row instanceof AliasRow && row.instance().hydrated,
    read: () => null,
    enabled: (row) => row.sheetReads.editable,
    persists: "backend",
    apply: async (row) => {
      if (!(row instanceof AliasRow)) return "not an alias row";
      await aliasWriterOf(row).reset();
      return null;
    },
  },
  {
    key: "row:lookup-key",
    label: "Lookup key",
    visibility: "advanced",
    group: null,
    control: { kind: "text" },
    reRender: "cells",
    applies: (row) => row instanceof SourceRow,
    read: (row) => row.lookupKey ?? null,
    enabled: (row) => row.sheetReads.editable,
    // lookupKey is server-owned (row identity for provider sources + formula
    // refs) — set via the server API, not a rows-side mutation. App-routed.
    persists: "local-only",
    apply: async () => "lookup key is set via the server API (app-routed)",
  },
  {
    // "Show Sync Logs" — observability toggle for a synced EXTERNAL source.
    // Flips the source's `showSyncLogs` attribute; the rows-side
    // `Sheet.reconcileSyncLogsChild` watches it and adds / drops a nested
    // `logs` child (`logType` derived from the `:~<provider>:` key), so the
    // re-render is structural (a row appears / disappears). The visible row
    // may BE the source, an alias / layout wrapper above it, or the source's
    // renderer child — `syncedSource` locates it in reach (see helper).
    key: "source:sync-logs",
    label: "Show Sync Logs",
    visibility: "menu",
    group: null,
    control: { kind: "toggle" },
    reRender: "structure",
    applies: (row) => syncedSource(row) != null,
    read: (row) => {
      const source = syncedSource(row);
      if (source == null) return null;
      return Json.boolean(source.attributes(), "showSyncLogs") === true;
    },
    // Observability view toggle (local-first attr) — available whenever a
    // synced source is in reach, independent of sheet editability.
    enabled: (row) => syncedSource(row) != null,
    persists: "local-only",
    apply: async (row, value) => {
      const source = syncedSource(row);
      if (source == null) return "no synced source row in reach";
      if (typeof value !== "boolean") return "sync logs requires a boolean";
      sheetStores(sheetOf(source)).rowMetadata.patchAttributes(source.id, {
        showSyncLogs: value,
      });
      return null;
    },
  },
];

const VISIBILITY_RANK: Record<RowEnvelopes.Visibility, number> = {
  shown: 0,
  menu: 1,
  advanced: 2,
};

// ── internal ─────────────────────────────────────────────────────────────

/**
 * The EXTERNAL synced `SourceRow` in reach of `row` — the row itself, a
 * direct child, or its immediate parent — or `null`. "External synced" =
 * a `SourceRow` whose RETAINED `:~` lookupKey names an external integration
 * (`RowProvider.isExternal` — excludes internal `lona`/`dbg` namespaces,
 * synthetic `:~dbg:data:*` demos, and non-canonical keys). The visible row
 * may BE the source, a transparent alias / layout wrapper whose source is a
 * direct child, or the source's renderer child (the source is then the
 * parent) — so we check self, children, then the immediate parent. Here in
 * the SDK we use `instanceof SourceRow` directly: it is the SDK's own
 * consistent variant identity (the app-side class-identity caveat that once
 * forced a `descriptor.role` check does not apply inside the SDK).
 */
function syncedSource(row: ModelRow): Option<SourceRow> {
  const isSynced = (r: ModelRow): r is SourceRow =>
    r instanceof SourceRow && RowProvider.isExternal(r.lookupKey);
  if (isSynced(row)) return row;
  for (const child of row.children) if (isSynced(child)) return child;
  const parent = row.parent;
  if (parent != null && isSynced(parent)) return parent;
  return null;
}

function optionEnvelope(
  row: ModelRow,
  c: ModelRowOptions.Contribution,
  visibility: RowEnvelopes.Visibility,
  enabled: boolean,
): RowEnvelopes.Envelope {
  return {
    key: c.key,
    label: c.label,
    visibility,
    group: c.section,
    input: toInput(c.control, ModelRowOptions.read(row.sheetReads, c.target)),
    enabled,
  };
}

/** Map a `ControlSpec` + current `Value` to the consumer-facing `Input`. */
function toInput(
  control: ModelRowOptions.ControlSpec,
  value: ModelRowOptions.Value,
): RowEnvelopes.Input {
  switch (control.kind) {
    case "action":
      return { kind: "action" };
    case "toggle":
      // Present the SHOWN value: a contribution may display the inverse of the
      // stored attr (e.g. "Monthly view" ON = `disableMonthlyView` false). The
      // consumer sends back a shown value; `apply` un-inverts before the write.
      return { kind: "toggle", value: control.invert === true ? value !== true : value === true };
    case "text":
      return { kind: "text", value: typeof value === "string" ? value : null };
    case "code":
      return {
        kind: "code",
        value: typeof value === "string" ? value : null,
        language: control.language,
      };
    case "color":
      return { kind: "color", value: typeof value === "string" ? value : "" };
    case "timezone":
      return {
        kind: "timezone",
        value: typeof value === "string" ? value : null,
      };
    case "range":
      return {
        kind: "number",
        value: typeof value === "number" ? value : null,
        min: control.min,
        max: control.max,
      };
    case "select":
      return {
        kind: "select",
        value: typeof value === "string" ? value : null,
        options: control.options,
      };
    case "time-range": {
      const range = isRange(value)
        ? value
        : { start: control.min, end: control.max };
      return { kind: "time-range", value: range, min: control.min, max: control.max };
    }
  }
}

function findContribution(
  model: ModelRowOptions.Model,
  key: string,
): Option<ModelRowOptions.Contribution> {
  const buckets: readonly ModelRowOptions.Contribution[][] = [
    [...model.primary],
    ...model.sections.map((s) => [...s.items]),
    [...model.overflow],
    [...model.advanced],
  ];
  for (const bucket of buckets) {
    for (const c of bucket) if (c.key === key) return c;
  }
  return null;
}

/** Light value validation against the control. Returns an error string or null. */
function validate(
  control: ModelRowOptions.ControlSpec,
  value: ModelRowOptions.Value,
): Option<string> {
  switch (control.kind) {
    case "action":
      return null; // value ignored
    case "toggle":
      return typeof value === "boolean" ? null : "expected a boolean";
    case "text":
    case "color":
    case "timezone":
      return value === null || typeof value === "string"
        ? null
        : "expected a string";
    case "code":
      return typeof value === "string" ? null : "expected a string";
    case "range":
      if (typeof value !== "number") return "expected a number";
      return value >= control.min && value <= control.max
        ? null
        : `out of range [${control.min}, ${control.max}]`;
    case "select":
      if (typeof value !== "string") return "expected a string";
      return control.options.some((o) => o.value === value)
        ? null
        : `not a valid option: "${value}"`;
    case "time-range":
      return isRange(value) ? null : "expected a { start, end } range";
  }
}

/** The envelopes of a row, read (`forRow`) or as the composed options only (`optionsFor`). */
export namespace RowEnvelopes {
  /**
   * INTERROGATE — every envelope for a row, in display order (visibility, then
   * insertion). Pure and DOM-free: the headless "virtually click the row".
   * The same list as `row.envelopes()`.
   */
  export function forRow(row: ModelRow): readonly Envelope[] {
    const out: Envelope[] = [];

    // 1+2: intrinsic + capability specs that apply to this row.
    for (const spec of SPECS) {
      if (!spec.applies(row)) continue;
      out.push({
        key: spec.key,
        label: spec.label,
        visibility: spec.visibility,
        group: spec.group,
        input: toInput(spec.control, spec.read(row)),
        enabled: spec.enabled(row),
      });
    }

    // 3: composed options (from the row's hydrated subtree).
    for (const e of optionsFor(row)) out.push(e);

    // Stable sort by visibility (shown < menu < advanced); insertion order
    // within a rank (intrinsics precede options).
    return out
      .map((e, i) => ({ e, i }))
      .sort((a, b) =>
        (VISIBILITY_RANK[a.e.visibility] - VISIBILITY_RANK[b.e.visibility]) ||
        (a.i - b.i)
      )
      .map(({ e }) => e);
  }

  /**
   * The composed OPTION envelopes for a row (no intrinsic / capability ones) —
   * the visible row's hydrated subtree composed via `ModelRowOptions.composeSheetRow`,
   * each resolved to its current value. The DOM options popover renders from
   * this; `forRow` includes it plus the intrinsic / capability envelopes.
   */
  export function optionsFor(row: ModelRow): readonly Envelope[] {
    const model = ModelRowOptions.composeSheetRow(row, null);
    const enabled = row.sheetReads.editable;
    const out: Envelope[] = [];
    for (const c of model.primary) out.push(optionEnvelope(row, c, "menu", enabled));
    for (const section of model.sections) {
      for (const c of section.items) {
        out.push(optionEnvelope(row, c, "menu", enabled));
      }
    }
    for (const c of model.overflow) out.push(optionEnvelope(row, c, "menu", enabled));
    for (const c of model.advanced) {
      out.push(optionEnvelope(row, c, "advanced", enabled));
    }
    return out;
  }

  /**
   * DRIVING a row's envelopes through the SDK row that owns them
   * (`row.envelopes`, `@tento/lona`): `peekAll()` to interrogate,
   * `apply(msg, actor)` to drive one.
   *
   * The one write path for an envelope. It runs the rows-side drive
   * (`RowEnvelopeDrive`), persists the intrinsic edits through the row's own
   * checked verb (`row.update({ label | heightPx }, actor)`), and mirrors the
   * visible-row attribute patch a composed option wrote into the row's
   * attribute store itself (`row.update({ attributes }, actor)`) — a caller
   * never mirrors by hand. A row's delete is not an envelope write:
   * `row:delete` resolves `"rejected"` and names
   * `handle.rows.delete(row, actor)`.
   */
  export class Driver {
    readonly #host: Driver.Host;
    readonly #assertLive: (verb: string) => void;

    /** The live row behind the host's `variant` face; `null` without one. */
    #liveVariant(): Option<ModelRow> {
      const variant = this.#host.variant;
      return variant == null ? null : liveRowOf(variant);
    }

    constructor(
      host: Driver.Host,
      /** Throws when the row's sheet was disposed (`SheetHandle.DisposedError`). */
      assertLive: (verb: string) => void,
    ) {
      this.#host = host;
      this.#assertLive = assertLive;
    }

    /**
     * Every envelope of the row, in display order — the same list as
     * `row.variant.envelopes()`; `[]` when the row has no rows-side model.
     * Synchronous: it reads the row as it is loaded.
     */
    peekAll(): readonly Envelope[] {
      return this.#liveVariant()?.envelopes() ?? [];
    }

    /**
     * Drive one envelope: resolve `msg.key`, validate `msg.value` and write it,
     * checked. Resolves `{ kind: "rejected", why }` — nothing changed, nothing
     * sent — for an unknown, invalid, disabled or app-routed message (never
     * thrown), otherwise `{ kind: "applied", persisted }` once every write it
     * made was accepted: the rows-side write, the intrinsic edit through
     * `row.update` (title, height) and the mirror of the visible-row attribute
     * patch into this row's attribute store. A write the backend refused or
     * never answered REJECTS with its `SdkRequestError` (`RowWriteError` for
     * the row's own update). `actor` stamps the row's own writes. On a row
     * whose sheet was disposed — this driver taken before or after — it
     * rejects with `SheetHandle.DisposedError`, changing and sending nothing.
     */
    async apply(msg: Message, actor: Actor): Promise<Applied> {
      this.#assertLive("row.envelopes.apply");
      const variant = this.#liveVariant();
      if (variant == null) {
        return { kind: "rejected", reRender: "cells", why: "the row has no rows-side model" };
      }
      const host = this.#host;
      const intrinsics: RowEnvelopeDrive.Intrinsics = {
        async setLabel(row: ModelRow, label: string): Promise<Option<string>> {
          await host.update({ label }, actor);
          await RowLocalEdits.setLabel(row, label);
          return null;
        },
        async setHeight(row: ModelRow, heightPx: number): Promise<Option<string>> {
          await host.update({ heightPx }, actor);
          await RowLocalEdits.setHeight(row, heightPx);
          return null;
        },
        remove(): Promise<Option<string>> {
          return Promise.resolve("delete the row with handle.rows.delete(row, actor)");
        },
        persisted(): Applied.Persisted {
          return "backend";
        },
      };
      const { applied, attrs } = await RowEnvelopeDrive.apply(variant, msg, intrinsics);
      if (applied.kind === "applied" && attrs != null) {
        await host.update({ attributes: { ...(host.attributes() ?? {}), ...attrs } }, actor);
      }
      return applied;
    }
  }

  export namespace Driver {
    /** The SDK row a `Driver` drives: its rows-side model row and its checked update. */
    export interface Host {
      /**
       * The rows-side model row behind the SDK row, as its reads face
       * (`null`: none attached); the driver takes the live row behind it.
       */
      readonly variant: Option<ModelRowReads>;
      /** The row's attribute bag as the SDK holds it. */
      attributes(): Option<JsonObject>;
      /** The row's checked update. */
      update(patch: RowPatch, actor: Actor): Promise<void>;
    }
  }
}

/**
 * The rows-side half of DRIVING an envelope — for the SDK row
 * (`sdkRow.envelopes.apply(msg, actor)`, `@tento/lona`), reached through
 * `@tento-lona/sheets/internal-sdk` (this repository's tests:
 * `@tento-lona/sheets/internal-testing`). Not on a published entry point: a
 * caller of `apply` would still have to persist the intrinsic edits and
 * mirror the visible-row attribute patch itself, which the SDK row does.
 */
export namespace RowEnvelopeDrive {
  /**
   * The option write behind an envelope's composed option: persist `value`
   * at `target` (a `ModelRowOptions.Target`) through its canonical write
   * path — an alias prop through the alias row's checked prop write, every
   * `*-attr` through the sheet's display-attribute seam (a renderer
   * attribute copy-on-write onto the materialized renderer), a formula
   * through the formula row's expression write. Rejects with the backend's
   * `SdkRequestError` when the write is refused; throws for a target no row
   * write owns (`app-pref`, a non-formula `source-metadata`). Off the
   * published `ModelRowOptions` (its read vocabulary): an application drives
   * the option through its SDK row, `row.envelopes.apply({ key, value },
   * actor)`.
   */
  export const writeTarget: (
    sheet: ModelSheet,
    target: ModelRowOptions.Target,
    value: ModelRowOptions.Value,
  ) => Promise<void> = writeOptionTarget;

  /**
   * How the intrinsic envelopes (title, height, delete) are carried out —
   * the SDK row's own checked verbs, or `LOCAL` (the local model only).
   * Each resolves `null` when done, or why it refused (nothing changed).
   */
  export interface Intrinsics {
    setLabel(row: ModelRow, label: string): Promise<Option<string>>;
    setHeight(row: ModelRow, heightPx: number): Promise<Option<string>>;
    remove(row: ModelRow): Promise<Option<string>>;
    /** Where an applied intrinsic edit lives. */
    persisted(row: ModelRow): RowEnvelopes.Applied.Persisted;
  }

  /** The intrinsic edits on the local model only (`RowLocalEdits`): no server verdict. */
  export const LOCAL: Intrinsics = Object.freeze({
    async setLabel(row: ModelRow, label: string): Promise<Option<string>> {
      await RowLocalEdits.setLabel(row, label);
      return null;
    },
    async setHeight(row: ModelRow, heightPx: number): Promise<Option<string>> {
      await RowLocalEdits.setHeight(row, heightPx);
      return null;
    },
    async remove(row: ModelRow): Promise<Option<string>> {
      await RowLocalEdits.remove(row);
      return null;
    },
    persisted(): RowEnvelopes.Applied.Persisted {
      return "local-only";
    },
  });

  /**
   * What `apply` did: the verdict a caller sees (`applied`) and the
   * VISIBLE-row attribute patch the write applied (`attrs`) — the patch the
   * SDK row mirrors into its own attribute store. `null` for actions,
   * intrinsic edits and renderer-attr options (copy-on-write to a child
   * renderer — not a visible-row attr).
   */
  export interface Result {
    readonly applied: RowEnvelopes.Applied;
    readonly attrs: Option<JsonObject>;
  }

  /**
   * DRIVE — apply a message: resolve `key`, validate `value` against its input,
   * perform the rows-side mutation, return how to re-render and what became of
   * the write (`Result`). A bad message never throws — it resolves
   * `{ kind: "rejected", why }` (so remote callers get a uniform result); a
   * write the backend refuses or never answers REJECTS with the backend's
   * `SdkRequestError`, like every checked verb. Intrinsic keys (title /
   * height / delete) route to `intrinsics`; capability keys to the spec's
   * mutation; option keys to the unified option write (`writeTarget`).
   */
  export async function apply(
    row: ModelRow,
    msg: RowEnvelopes.Message,
    intrinsics: Intrinsics,
  ): Promise<Result> {
    const spec = SPECS.find((s) => s.key === msg.key);
    if (spec !== undefined) {
      if (!spec.applies(row)) {
        return rejected("cells", `envelope "${msg.key}" does not apply to this row`);
      }
      if (!spec.enabled(row)) {
        return rejected("cells", `envelope "${msg.key}" is not editable`);
      }
      const invalid = validate(spec.control, msg.value);
      if (invalid != null) return rejected("cells", invalid);
      let refusal: Option<string>;
      try {
        refusal = await spec.apply(row, msg.value, intrinsics);
      } catch (err) {
        return failedOrRejected(spec.reRender, err);
      }
      if (refusal != null) return rejected(spec.reRender, refusal);
      return {
        applied: {
          kind: "applied",
          reRender: spec.reRender,
          persisted: spec.persists === "local-only"
            ? "local-only"
            : spec.persists === "intrinsic"
            ? intrinsics.persisted(row)
            : persistedOn(row),
        },
        attrs: null,
      };
    }

    const model = ModelRowOptions.composeSheetRow(row, null);
    const contribution = findContribution(model, msg.key);
    if (contribution == null) {
      return rejected("cells", `no envelope "${msg.key}" on row ${row.id.rawId}`);
    }
    const invalid = validate(contribution.control, msg.value);
    if (invalid != null) return rejected("cells", invalid);
    // Un-invert a toggle whose contribution displays the inverse of the stored
    // attr (`toInput` presented the shown value; the store keeps the raw one).
    const writeValue =
      contribution.control.kind === "toggle" && contribution.control.invert === true
        ? msg.value !== true
        : msg.value;
    // A renderer-attr write against a STILL-SYNTHETIC default renderer
    // materializes it into a real row (copy-on-write) — a STRUCTURAL change, so
    // the host must re-join + rebuild the tree, not just re-bind cells. Detect it
    // from the pre-write target id and elevate the re-render; subsequent writes
    // hit the real (non-synthetic) renderer and keep the declared `reRender`.
    // (Without this the first Format change repaints only some cells — issues
    // renderer-cow RC-1.)
    const materializes = contribution.target.kind === "renderer-attr" &&
      isSyntheticDefaultRendererId(contribution.target.rowId);
    try {
      await writeTarget(sheetOf(row), contribution.target, writeValue);
    } catch (err) {
      return failedOrRejected(
        materializes ? "structure" : (contribution.reRender ?? "cells"),
        err,
      );
    }
    // Hand back the VISIBLE-row attr patch: the SDK row mirrors it into its own
    // attribute store and persists it (`sdkRow.envelopes.apply`). A renderer-attr is
    // copy-on-write to a CHILD renderer — not a visible-row attr — so no mirror.
    const t = contribution.target;
    let attrs: Option<JsonObject>;
    if (t.kind === "renderer-attr") {
      attrs = null;
    } else if (t.kind === "alias-child-attr" || t.kind === "alias-child-attr-range") {
      // `writeTarget` already merged the child slot into the alias's
      // `children` map (rows-side). Hand the FULL map to the mirror so it
      // persists on the visible alias row — a flat top-level patch would
      // split-brain the override slot (see `attrsFor`).
      const aliasChildren = Json.child(
        row.sheetReads.peekRow(t.rowId)?.attributes() ?? null,
        "children",
      );
      attrs = aliasChildren != null ? { children: aliasChildren } : null;
    } else {
      attrs = ModelRowOptions.attrsFor(t, writeValue);
    }
    return {
      applied: {
        kind: "applied",
        reRender: materializes ? "structure" : (contribution.reRender ?? "cells"),
        persisted: persistedOn(row),
      },
      attrs,
    };
  }

  /** A message that was not applied: nothing changed, nothing was sent. */
  function rejected(reRender: ModelRowOptions.ReRender, why: string): Result {
    return { applied: { kind: "rejected", reRender, why }, attrs: null };
  }

  /**
   * A write that threw: a backend verdict (`SdkRequestError`) is rethrown —
   * `apply` rejects with it; any other throw is the write refusing the
   * message before it sent anything (a target of the wrong kind, a formula
   * cycle) — a `"rejected"`.
   */
  function failedOrRejected(reRender: ModelRowOptions.ReRender, err: unknown): Result {
    if (err instanceof SdkRequestError) throw err;
    return rejected(reRender, err instanceof Error ? err.message : String(err));
  }

  /** Where a backend write on `row`'s sheet landed: an ephemeral sheet defers it. */
  function persistedOn(row: ModelRow): RowEnvelopes.Applied.Persisted {
    return sheetOf(row).ephemeral ? "deferred-to-promote" : "backend";
  }
}
